import { liquidTone } from '../components/LiquidText';
import { codigoEquipamento } from '../utils/codigoEquipamento';
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import './Relatorios.css';
import { useMaquetesTheme } from '../context/MaquetesThemeContext';
import ReportThemeIcon from '../components/ReportThemeIcon';
import { OrcamentoFeedbackProvider, useOrcamentoFeedback } from '../components/OrcamentoUI';

// `embutido` — quando esta lista é mostrada dentro de outro ecrã (o separador
// Instalações e Equipamentos do Sistema Integrado), esconde-se o cabeçalho da
// página. É o mesmo componente e os mesmos dados, não uma segunda cópia.
const RelatoriosContent = ({ embutido = false }) => {
    const { notify } = useOrcamentoFeedback();
    const { active: themed } = useMaquetesTheme();
    const cleanIcons = themed && !embutido;
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState('todos');
    const [dl50Records, setDl50Records] = useState([]);
    const [andaimeRecords, setAndaimeRecords] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filtro, setFiltro] = useState('');
    const [ano, setAno] = useState('todos');

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            try {
                const [dl50Res, andaimesRes] = await Promise.all([
                    api.get('/dl50').catch(() => ({ data: [] })),
                    api.get('/andaimes').catch(() => ({ data: [] }))
                ]);
                setDl50Records((dl50Res.data || []).map(r => ({ ...r, _tipo: 'DL50' })));
                setAndaimeRecords((andaimesRes.data || []).map(r => ({ ...r, _tipo: 'Andaime' })));
            } catch (err) {
                console.error('Erro ao carregar relatórios:', err);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, []);

    const handleViewPdf = async (tipo, id) => {
        try {
            const endpoint = tipo === 'DL50' ? `/dl50/relatorio/${id}` : `/andaimes/relatorio/${id}`;
            const response = await api.get(endpoint, { responseType: 'blob' });
            const blob = response.data;
            const url = window.URL.createObjectURL(blob);
            window.open(url, '_blank');
        } catch (err) {
            console.error('Erro ao abrir PDF:', err);
            notify('Não foi possível abrir o PDF. ' + (err.response?.status === 404 ? 'Relatório não encontrado.' : err.message));
        }
    };

    // Verificação transcrita de papel: o botão abre a digitalização do formulário
    // assinado, guardada no servidor, e não o relatório gerado pela aplicação.
    const handleViewOriginal = async (id) => {
        try {
            const response = await api.get(`/dl50/${id}/original`, { responseType: 'blob' });
            const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
            window.open(url, '_blank');
        } catch (err) {
            console.error('Erro ao abrir original:', err);
            notify('Não foi possível abrir o formulário digitalizado.');
        }
    };

    const handleShare = async (tipo, id, shareToken) => {
        const endpoint = tipo === 'DL50' ? `/dl50/relatorio/${id}` : `/andaimes/relatorio/${id}`;
        const fullUrl = window.location.origin + '/api' + endpoint + (shareToken ? `?t=${shareToken}` : '');
        if (navigator.share) {
            try {
                await navigator.share({
                    title: `Relatório ${tipo}`,
                    text: 'Consulte o relatório neste link:',
                    url: fullUrl
                });
            } catch (err) { /* cancelado */ }
        } else {
            window.open(fullUrl, '_blank');
        }
    };

    // Merge and filter
    let allRecords = [];
    if (activeTab === 'todos' || activeTab === 'dl50') {
        allRecords = [...allRecords, ...dl50Records];
    }
    if (activeTab === 'todos' || activeTab === 'andaimes') {
        allRecords = [...allRecords, ...andaimeRecords];
    }

    // Anos com verificações registadas, do mais recente para o mais antigo, com
    // a contagem de cada um — calculados sobre tudo, para o seletor não perder
    // um ano só porque o separador ativo não tem registos nele.
    const contagemPorAno = {};
    [...dl50Records, ...andaimeRecords].forEach(r => {
        if (!r.data_verificacao) return;
        const a = new Date(r.data_verificacao).getFullYear();
        contagemPorAno[a] = (contagemPorAno[a] || 0) + 1;
    });
    const anosDisponiveis = Object.keys(contagemPorAno).map(Number).sort((x, y) => y - x);

    if (ano !== 'todos') {
        allRecords = allRecords.filter(r =>
            r.data_verificacao && new Date(r.data_verificacao).getFullYear() === Number(ano));
    }

    // Sort by date descending
    allRecords.sort((a, b) => new Date(b.data_verificacao) - new Date(a.data_verificacao));

    // Search filter
    if (filtro) {
        const termo = filtro.toLowerCase();
        allRecords = allRecords.filter(r =>
            (r.relatorio_num || '').toLowerCase().includes(termo) ||
            (r.obra_nome || '').toLowerCase().includes(termo) ||
            (r.equip_nome || '').toLowerCase().includes(termo) ||
            (r._tipo === 'DL50' && r.equipamento_id && codigoEquipamento(r.equipamento_id).toLowerCase().includes(termo)) ||
            (r.nome_tecnico || '').toLowerCase().includes(termo) ||
            (r._tipo || '').toLowerCase().includes(termo)
        );
    }

    return (
        <div className={embutido ? '' : 'relatorios-page'}>
            {!embutido && (
                <div className="top-bar">
                    <div className="top-bar-left">
                        <div className="top-bar-title">
                            <h1>{cleanIcons ? <ReportThemeIcon name="chart" /> : '📊'} Relatórios</h1>
                            <p>Consulta de todos os relatórios gerados</p>
                        </div>
                    </div>
                    <div className="top-bar-actions">
                        <button className="btn-primary" onClick={() => navigate('/andaime-verificacao')}>
                            {cleanIcons ? <ReportThemeIcon name="plus" /> : '+'} Nova Verif. Andaime
                        </button>
                    </div>
                </div>
            )}

            <div className={embutido ? '' : 'page-content'}>
                {/* Tabs */}
                <div className="shared-subnav">
                    <button className={`shared-subnav-btn ${activeTab === 'todos' ? 'active' : ''}`} onClick={() => setActiveTab('todos')}>Todos</button>
                    <button className={`shared-subnav-btn ${activeTab === 'dl50' ? 'active' : ''}`} onClick={() => setActiveTab('dl50')}>DL50</button>
                    <button className={`shared-subnav-btn ${activeTab === 'andaimes' ? 'active' : ''}`} onClick={() => setActiveTab('andaimes')}>Andaimes</button>
                </div>

                <div className="rel-list-panel">
                <div className="rel-filters">
                {/* Ano + Search */}
                <div className="rel-year-filter" style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 10 }}>
                    <label htmlFor="rel-year" style={{ fontSize: '0.74rem', color: '#64748b', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.03em' }}>
                        Ano
                    </label>
                    <select id="rel-year" value={ano} onChange={(e) => setAno(e.target.value)}
                        style={{ padding: '7px 10px', border: '1px solid #cbd5e1', borderRadius: 8, fontSize: '0.9rem' }}>
                        <option value="todos">Todos os anos</option>
                        {anosDisponiveis.map(a => (
                            <option key={a} value={a}>{a} ({contagemPorAno[a]})</option>
                        ))}
                    </select>
                </div>

                <div className="shared-search">
                    <input
                        type="text"
                        aria-label="Pesquisar relatórios"
                        placeholder="Pesquisar por nº, obra, equipamento, técnico..."
                        value={filtro}
                        onChange={(e) => setFiltro(e.target.value)}
                    />
                </div>

                </div>
                {/* Table / Cards */}
                {loading ? (
                    <div className="rel-loading">A carregar relatórios...</div>
                ) : allRecords.length === 0 ? (
                    <div className="rel-empty">
                        <p>Nenhum relatório encontrado.</p>
                    </div>
                ) : (
                    <>
                        {/* Desktop Table */}
                        <div className="rel-table-wrapper">
                            <table className="rel-table standard-table">
                                <thead>
                                    <tr>
                                        <th>Tipo</th>
                                        <th>Nº Relatório</th>
                                        <th>Data</th>
                                        <th>Obra</th>
                                        <th>Equipamento</th>
                                        <th>Técnico</th>
                                        <th>Resultado</th>
                                        <th>Ações</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {allRecords.map(r => (
                                        <tr key={`${r._tipo}-${r.id}`}>
                                            <td>
                                                <span className={`rel-tipo-badge ${r._tipo === 'DL50' ? 'tipo-dl50' : 'tipo-andaime'}`}>
                                                    {cleanIcons && <ReportThemeIcon name={r._tipo === 'DL50' ? 'dl50' : 'andaime'} />}
                                                    {r._tipo}
                                                </span>
                                            </td>
                                            <td className="rel-num">
                                                {r.relatorio_num || '-'}
                                                {r.origem === 'transcricao' && (
                                                    <span data-liquid-tone={liquidTone('#fef3c7')} data-liquid-badge="true" className="rel-transcription" title={r.origem_descricao || 'Transcrição de formulário em papel'}
                                                        style={{
                                                            marginLeft: 6, padding: '2px 7px', borderRadius: 10,
                                                            fontSize: '0.68rem', fontWeight: 700,
                                                            background: '#fef3c7', color: '#92400e', whiteSpace: 'nowrap',
                                                        }}>Transcrição</span>
                                                )}
                                            </td>
                                            <td>{r.data_verificacao ? new Date(r.data_verificacao).toLocaleDateString('pt-PT') : '-'}</td>
                                            <td>{r.obra_nome || '-'}</td>
                                            <td>{r._tipo === 'DL50' && r.equipamento_id ? `${codigoEquipamento(r.equipamento_id)} · ${r.equip_nome || '-'}` : r.equip_nome || '-'}</td>
                                            <td>{r.nome_tecnico || '-'}</td>
                                            <td>
                                                <span className={`rel-resultado ${r.resultado_usavel ? 'apto' : 'nao-apto'}`}>
                                                    {cleanIcons ? <><ReportThemeIcon name={r.resultado_usavel ? 'check' : 'cross'} />{r.resultado_usavel ? 'Apto' : 'Não apto'}</> : (r.resultado_usavel ? '✅ Apto' : '❌ Não Apto')}
                                                </span>
                                            </td>
                                            <td className="rel-actions-cell">
                                                {r.origem === 'transcricao' && r.ficheiro_origem_url && (
                                                    <button className="rel-btn" onClick={() => handleViewOriginal(r.id)}
                                                        title="Ver o formulário em papel assinado" aria-label="Ver o formulário em papel assinado">
                                                        {cleanIcons ? <ReportThemeIcon name="original" /> : '📎'}
                                                    </button>
                                                )}
                                                <button className="rel-btn rel-btn-pdf" onClick={() => handleViewPdf(r._tipo, r.id)} title="Ver PDF" aria-label="Ver PDF">
                                                    {cleanIcons ? <ReportThemeIcon name="pdf" /> : '🖨️'}
                                                </button>
                                                <button className="rel-btn rel-btn-share" onClick={() => handleShare(r._tipo, r.id, r.share_token)} title="Partilhar" aria-label="Partilhar">
                                                    {cleanIcons ? <ReportThemeIcon name="share" /> : '📤'}
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        {/* Mobile Cards */}
                        <div className="rel-cards-mobile">
                            {allRecords.map(r => (
                                <div key={`${r._tipo}-${r.id}`} className="rel-card">
                                    <div className="rel-card-top">
                                        <span className={`rel-tipo-badge ${r._tipo === 'DL50' ? 'tipo-dl50' : 'tipo-andaime'}`}>
                                            {cleanIcons && <ReportThemeIcon name={r._tipo === 'DL50' ? 'dl50' : 'andaime'} />}
                                                    {r._tipo}
                                        </span>
                                        <span className="rel-card-num">{r.relatorio_num || '-'}</span>
                                        <span className={`rel-resultado ${r.resultado_usavel ? 'apto' : 'nao-apto'}`}>
                                            {cleanIcons ? <><ReportThemeIcon name={r.resultado_usavel ? 'check' : 'cross'} />{r.resultado_usavel ? 'Apto' : 'Não apto'}</> : (r.resultado_usavel ? '✅' : '❌')}
                                        </span>
                                    </div>
                                    <div className="rel-card-body">
                                        <p>{cleanIcons ? <ReportThemeIcon name="building" /> : '🏗️'} {r.obra_nome || '-'}</p>
                                        <p>{cleanIcons ? <ReportThemeIcon name="equipment" /> : '🔧'} {r._tipo === 'DL50' && r.equipamento_id ? `${codigoEquipamento(r.equipamento_id)} · ${r.equip_nome || '-'}` : r.equip_nome || '-'}</p>
                                        <p>{cleanIcons ? <ReportThemeIcon name="calendar" /> : '📅'} {r.data_verificacao ? new Date(r.data_verificacao).toLocaleDateString('pt-PT') : '-'}</p>
                                    </div>
                                    <div className="rel-card-actions">
                                        <button className="rel-btn-full rel-btn-pdf" onClick={() => handleViewPdf(r._tipo, r.id)}>
                                            {cleanIcons ? <ReportThemeIcon name="pdf" /> : '🖨️'} Ver PDF
                                        </button>
                                        <button className="rel-btn-full rel-btn-share" onClick={() => handleShare(r._tipo, r.id, r.share_token)}>
                                            {cleanIcons ? <ReportThemeIcon name="share" /> : '📤'} Partilhar
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </>
                )}

                <div className="rel-summary">
                    <span>Total: <strong>{allRecords.length}</strong> relatório(s)</span>
                </div>
                </div>
            </div>
        </div>
    );
};

const Relatorios = props => props.embutido ? <RelatoriosContent {...props} /> : <OrcamentoFeedbackProvider><RelatoriosContent {...props} /></OrcamentoFeedbackProvider>;
export default Relatorios;
